import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import CloseIcon from '@mui/icons-material/Close';

import { downloadBlobFile, captureError, formatDateTime } from '@clatter/platform';
import { addNotification } from '../../../store';
import { useSlideThumbnailPolling } from '../../../hooks';
import { useGetSlideDownloadMutation } from '../../../services';
import { buildSlideKey, buildThumbnailUrl } from '../../../helpers';

const StyledDialogOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: ${({ theme }) => theme.dialog.overlayBackground};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const StyledDialogContent = styled.div`
  background: ${({ theme }) => theme.palette.white};
  border-radius: ${({ theme }) => theme.dialog.borderRadius};
  max-width: ${({ theme }) => theme.dialog.maxWidth};
  width: 90%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: ${({ theme }) => theme.shadows.xl};
`;

const StyledDialogHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.dialog.headerPadding};
  border-bottom: 1px solid ${({ theme }) => theme.palette.border};
  flex-shrink: 0;
  gap: 16px;
`;

const StyledBreadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.palette.textDimmed};
  font-weight: 500;
  overflow: hidden;
  min-width: 0;
`;

const StyledBreadcrumbSegment = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledBreadcrumbSeparator = styled.span`
  color: ${({ theme }) => theme.palette.cardBorderDarker};
  flex-shrink: 0;
`;

const StyledBreadcrumbCurrent = styled.span`
  color: ${({ theme }) => theme.palette.textStrong};
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  flex-shrink: 0;
`;

const StyledDownloadButton = styled.button`
  padding: 7px 14px;
  border-radius: 7px;
  border: 1.5px solid ${({ theme }) => theme.palette.border};
  background: ${({ theme }) => theme.palette.white};
  color: ${({ theme }) => theme.palette.textMedium};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: border-color 0.15s, background 0.15s;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.palette.cardBorderDarker};
    background: ${({ theme }) => theme.palette.cardSurface};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StyledAddButton = styled.button<{ $isAdded: boolean }>`
  padding: 7px 14px;
  border-radius: 7px;
  border: none;
  background: ${({ $isAdded, theme }) => ($isAdded ? theme.palette.textDimmed : theme.palette.linkHoverColor)};
  color: ${({ theme }) => theme.palette.white};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  white-space: nowrap;
  transition: background 0.15s;

  &:hover {
    background: ${({ $isAdded, theme }) => ($isAdded ? theme.palette.textMuted : '#0057cc')};
  }
`;

const StyledCloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.palette.textDimmed};
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    font-size: 24px;
  }

  &:hover {
    color: ${({ theme }) => theme.palette.textStrong};
  }
`;

const StyledDialogBody = styled.div`
  flex: 1;
  overflow: auto;
  padding: ${({ theme }) => theme.dialog.bodyPadding};
  display: flex;
  flex-direction: column;
`;

const StyledBodyInner = styled.div`
  display: flex;
  gap: 24px;
  flex: 1;
`;

const StyledInfoPanel = styled.div`
  width: 256px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
`;

const StyledInfoTitle = styled.h4`
  font-weight: 600;
  color: ${({ theme }) => theme.palette.textStrong};
  margin-bottom: 16px;
  font-size: ${({ theme }) => theme.fontSize.h5};
`;

const StyledInfoLabel = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.palette.textStrong};
  display: block;
  margin-bottom: ${({ theme }) => theme.spacing.small};
  font-size: ${({ theme }) => theme.fontSize.body};
`;

const StyledInfoValue = styled.span`
  color: ${({ theme }) => theme.palette.textDimmed};
  font-size: ${({ theme }) => theme.fontSize.body};
`;

const StyledInfoDivider = styled.div`
  margin-top: auto;
  padding-top: ${({ theme }) => theme.dialog.bodyPadding};
  border-top: 1px solid ${({ theme }) => theme.palette.stroke};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.small};
`;

const StyledInfoLink = styled.a`
  color: ${({ theme }) => theme.palette.textLinkColor};
  text-decoration: none;
  font-size: ${({ theme }) => theme.fontSize.body};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const StyledPreviewPanel = styled.div`
  flex: 1;
  min-width: 0;
`;

const StyledSlidePreviewContainer = styled.div`
  aspect-ratio: 16 / 9;
  background: ${({ theme }) => theme.palette.cardSurface};
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme }) => theme.palette.stroke};
  border-radius: ${({ theme }) => theme.radius.card};
  overflow: hidden;
`;

const StyledPreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const StyledDialogFooter = styled.div`
  border-top: 1px solid ${({ theme }) => theme.palette.stroke};
  padding: ${({ theme }) => theme.dialog.footerPadding};
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
`;

const StyledNavButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  border-radius: ${({ theme }) => theme.radius.button};
  border: 1.5px solid ${({ theme }) => theme.palette.stroke};
  background: ${({ theme }) => theme.palette.white};
  color: ${({ theme }) => theme.palette.textMedium};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: border-color 0.15s, background 0.15s;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.palette.cardBorderDarker};
    background: ${({ theme }) => theme.palette.cardSurface};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const StyledSlideCounter = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.palette.textDimmed};
`;

const folderItemToSlideWithPath = (item: FolderManagerItem, deckName: string): SlideWithPath => ({
  slide: {
    SlideID: item.itemData.ID,
    Title: item.displayName || item.name || 'Slide',
    LayoutName: item.itemData?.LayoutName ?? undefined,
    ThumbnailLocation: item.itemData?.ThumbnailLocation ?? undefined,
    Author: item.itemData?.Author ?? undefined,
    CreatedAt: item.itemData?.CreatedAt ?? undefined,
    UpdatedAt: item.itemData?.UpdatedAt ?? undefined,
  },
  path: [deckName],
});

type SlidePreviewDialogProps =
  | {
      slidesWithPaths: SlideWithPath[];
      folderSlides?: never;
      deckName?: never;
      currentIndex: number;
      selectedKeys: Set<string>;
      readOnly?: boolean;
      hideAddButton?: boolean;
      selectedThemeId?: number | null;
      onClose: () => void;
      onNavigate: (index: number) => void;
      onToggle: (slides: OutlineSlide[], checked: boolean) => void;
    }
  | {
      slidesWithPaths?: never;
      folderSlides: FolderManagerItem[];
      deckName: string;
      currentIndex: number;
      selectedKeys?: Set<string>;
      readOnly?: boolean;
      hideAddButton?: boolean;
      selectedThemeId?: number | null;
      onClose: () => void;
      onNavigate: (index: number) => void;
      onToggle?: (slides: OutlineSlide[], checked: boolean) => void;
    };

export const SlidePreviewDialog: React.FC<SlidePreviewDialogProps> = ({
  slidesWithPaths: slidesWithPathsProp,
  folderSlides,
  deckName,
  currentIndex,
  selectedKeys,
  readOnly = false,
  hideAddButton = false,
  selectedThemeId,
  onClose,
  onNavigate,
  onToggle,
}) => {
  const dispatch = useDispatch();
  const [downloadSlide, { isLoading: isDownloading }] = useGetSlideDownloadMutation();

  const slidesWithPaths = slidesWithPathsProp ?? (folderSlides || []).map((item) => folderItemToSlideWithPath(item, deckName ?? ''));

  const { slide, path } = slidesWithPaths[currentIndex];
  const title = slide.Title || 'Slide';
  const baseThumbnailUrl = buildThumbnailUrl(slide.ThumbnailLocation, selectedThemeId);
  const { thumbnailUrl } = useSlideThumbnailPolling(baseThumbnailUrl, true);
  const isSelected = selectedKeys ? selectedKeys.has(buildSlideKey(slide.SlideID)) : false;
  const handleToggleSlide = () => {
    onToggle?.([slide], !isSelected);
  };

  const handleDownloadSlide = async () => {
    if (!slide.SlideID) return;
    try {
      const blob = await downloadSlide({ slideId: slide.SlideID }).unwrap();
      if (blob) {
        downloadBlobFile(blob, `${title}.pptx`);
      }
    } catch (error) {
      captureError(error, { message: 'Failed to download slide', component: 'SlidePreviewDialog' });
      dispatch(addNotification({ type: 'error', message: 'Slide download failed. Please try again.' }));
    }
  };

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowLeft' && currentIndex > 0) onNavigate(currentIndex - 1);
      if (event.key === 'ArrowRight' && currentIndex < slidesWithPaths.length - 1) onNavigate(currentIndex + 1);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, slidesWithPaths.length, onClose, onNavigate]);

  return ReactDOM.createPortal(
    <StyledDialogOverlay onClick={handleOverlayClick}>
      <StyledDialogContent>
        <StyledDialogHeader>
          <StyledBreadcrumb>
            {path.map((segment, index) => (
              <React.Fragment key={index}>
                <StyledBreadcrumbSegment>{segment}</StyledBreadcrumbSegment>
                <StyledBreadcrumbSeparator>›</StyledBreadcrumbSeparator>
              </React.Fragment>
            ))}
            <StyledBreadcrumbCurrent>{title}</StyledBreadcrumbCurrent>
          </StyledBreadcrumb>

          <StyledHeaderActions>
            {/* @todo: replace with ngButton */}
            <StyledDownloadButton onClick={handleDownloadSlide} disabled={isDownloading || !slide.SlideID}>
              {isDownloading ? 'Downloading…' : 'Download'}
            </StyledDownloadButton>

            {/* <NgButton
              variant={buttonVariants.clear}
              width={buttonWidths.sm}
              onClick={handleDownloadSlide}
              disabled={isDownloading}
              isLoading={isDownloading}
            >
              {isDownloading ? 'Downloading…' : 'Download'}
            </NgButton> */}

            {/* @todo: replace with ngButton */}
            {!hideAddButton && (
              <StyledAddButton $isAdded={isSelected} disabled={readOnly} onClick={handleToggleSlide}>
                {isSelected ? 'Remove from Presentation' : 'Add to Presentation'}
              </StyledAddButton>
            )}

            <StyledCloseButton onClick={onClose}><CloseIcon /></StyledCloseButton>
          </StyledHeaderActions>
        </StyledDialogHeader>

        <StyledDialogBody>
          <StyledBodyInner>
            <StyledInfoPanel>
              <StyledInfoTitle>{title}</StyledInfoTitle>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <StyledInfoLabel>Created Date:</StyledInfoLabel>
                  <StyledInfoValue>{slide.CreatedAt ? formatDateTime(slide.CreatedAt) : 'N/A'}</StyledInfoValue>
                </div>
                <div>
                  <StyledInfoLabel>Modified Date:</StyledInfoLabel>
                  <StyledInfoValue>{slide.UpdatedAt ? formatDateTime(slide.UpdatedAt) : 'N/A'}</StyledInfoValue>
                </div>
                <div>
                  <StyledInfoLabel>Author:</StyledInfoLabel>
                  <StyledInfoValue>{slide.Author || 'N/A'}</StyledInfoValue>
                </div>
                <div>
                  <StyledInfoLabel>Deck:</StyledInfoLabel>
                  <StyledInfoValue>{deckName ?? 'N/A'}</StyledInfoValue>
                </div>
              </div>

              {/* <StyledInfoDivider>
                <StyledInfoLink
                  href="#"
                  onClick={(event) => {
                    event.preventDefault();
                    console.log('Share slide', slide);
                  }}
                >
                  Share this slide
                </StyledInfoLink>
                <StyledInfoLink
                  href="#"
                  onClick={(event) => {
                    event.preventDefault();
                    console.log('Share entire deck', deckName);
                  }}
                >
                  Share entire deck
                </StyledInfoLink>
              </StyledInfoDivider> */}
            </StyledInfoPanel>

            <StyledPreviewPanel>
              <StyledSlidePreviewContainer>
                {thumbnailUrl ?? baseThumbnailUrl ? (
                  <StyledPreviewImage src={(thumbnailUrl ?? baseThumbnailUrl)!} alt={title} />
                ) : (
                  <StyledSlideCounter>No preview available</StyledSlideCounter>
                )}
              </StyledSlidePreviewContainer>
            </StyledPreviewPanel>
          </StyledBodyInner>
        </StyledDialogBody>

        <StyledDialogFooter>
          <StyledNavButton disabled={currentIndex <= 0} onClick={() => onNavigate(currentIndex - 1)}>
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Previous
          </StyledNavButton>

          <StyledSlideCounter>
            Slide {currentIndex + 1} of {slidesWithPaths.length}
          </StyledSlideCounter>

          <StyledNavButton
            disabled={currentIndex >= slidesWithPaths.length - 1}
            onClick={() => onNavigate(currentIndex + 1)}
          >
            Next
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </StyledNavButton>
        </StyledDialogFooter>
      </StyledDialogContent>
    </StyledDialogOverlay>,
    document.body,
  );
};
